import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import apiConfig from "constants/apiConfig";
import { MSG91_WIDGET_TOKEN } from "constants/global";
import { fetchChatSupportTokenThunk } from "slices/auth/thunk";
import { AppDispatch, RootState } from "store";
import { isFresh } from "utils/helpers";
import {
    finalizeMsg91WidgetInit,
    hideMsg91ChatWidget,
    initMsg91ChatWidget,
    loadMsg91ChatScript,
    setMsg91WidgetReady,
} from "utils/msg91Chat";

const CHAT_TOKEN_STALE_TIME = apiConfig.CHAT_SUPPORT_TOKEN.staleTime;
const STALE_CHECK_INTERVAL_MS = 60 * 60 * 1000;

interface UseMsg91ChatWidgetOptions {
    enabled?: boolean;
}

const buildHelloConfig = (
    token: string,
    selectedAgencyId: string,
    userName: string,
    agencyName: string,
    userMail: string
): Msg91HelloConfig => ({
    widgetToken: MSG91_WIDGET_TOKEN,
    user_jwt_token: token,
    unique_id: selectedAgencyId,
    name: `${userName} - ${agencyName}`,
    mail: userMail,
    show_widget_form: false,
    hide_launcher: true,
    launch_widget: false,
    show_close_button: true,
    show_send_button: true,
    theme: "system",
});

export default function useMsg91ChatWidget({ enabled = true }: UseMsg91ChatWidgetOptions = {}) {
    const dispatch = useDispatch<AppDispatch>();
    const store = useStore<RootState>();

    const user = useSelector((state: RootState) => state.auth.user);
    const selectedAgencyId = useSelector((state: RootState) => state.auth.selectedAgencyId);
    const selectedAgency = useSelector((state: RootState) => state.auth.selectedAgency);

    const initInFlightRef = useRef(false);
    const initializedRef = useRef(false);

    const initWidget = useCallback(
        async (forceTokenRefresh: boolean) => {
            if (!enabled || !MSG91_WIDGET_TOKEN || !user || !selectedAgencyId || !selectedAgency) {
                return;
            }

            if (!forceTokenRefresh && initializedRef.current) return;
            if (initInFlightRef.current) return;

            initInFlightRef.current = true;
            setMsg91WidgetReady(false);

            try {
                const result = await dispatch(fetchChatSupportTokenThunk(forceTokenRefresh)).unwrap();

                const scriptReady = await loadMsg91ChatScript();
                if (!scriptReady) {
                    console.error("[MSG91] Chat widget script failed to load");
                    return;
                }

                initMsg91ChatWidget(
                    buildHelloConfig(
                        result.token,
                        selectedAgencyId,
                        user.name,
                        selectedAgency.name,
                        user.username
                    ),
                    0
                );
                initializedRef.current = true;
                finalizeMsg91WidgetInit();
            } catch (error) {
                console.error("[MSG91] Failed to initialize chat widget", error);
            } finally {
                initInFlightRef.current = false;
            }
        },
        [enabled, dispatch, user, selectedAgencyId, selectedAgency]
    );

    useEffect(() => {
        if (!enabled) {
            hideMsg91ChatWidget();
            setMsg91WidgetReady(false);
            initializedRef.current = false;
            return;
        }

        void initWidget(false);
    }, [enabled, initWidget]);

    useEffect(() => {
        if (!enabled || !user || !selectedAgencyId) return;

        const intervalId = window.setInterval(() => {
            const state = store.getState();
            const agencyId = state.auth.selectedAgencyId;
            if (!agencyId) return;

            const cached = state.auth.chat_support_tokens[agencyId];
            if (cached && isFresh(cached.generatedAt, CHAT_TOKEN_STALE_TIME)) return;

            initializedRef.current = false;
            void initWidget(true);
        }, STALE_CHECK_INTERVAL_MS);

        return () => window.clearInterval(intervalId);
    }, [enabled, user?.uuid, selectedAgencyId, store, initWidget]);
}
